import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, CheckCircle2, FolderKanban, Table2, BookOpen } from 'lucide-react';
import { onboardingApi, profilingApi } from '../../services/api';
import type { OnboardingState } from '../../types';

interface Props {
  state: OnboardingState;
  setProjectName: (n: string) => void;
  setGroupName: (n: string) => void;
  onBack: () => void;
}

const SaveToProject: React.FC<Props> = ({ state, setProjectName, setGroupName, onBack }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<{ project_id: number; group_id: number; rules_count: number } | null>(null);

  const enabledRules = state.suggestedRules.filter((r) => r.enabled);

  const handleSave = async () => {
    if (!state.projectName.trim() || !state.groupName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const resp = await onboardingApi.saveProject({
        project_name: state.projectName,
        group_name: state.groupName,
        connection_type: state.connectorType || '',
        rules: enabledRules,
        connection: state.connection || {},
        schema: state.selectedSchema,
        tables: state.selectedTables?.map((t: any) => t.table_name || t) || [],
      });
      setSaved(resp.data.payload);

      // Save full session data to MongoDB for history
      try {
        const tables = state.selectedTables.map(t => t.table_name);
        const columns = state.profileResult?.tables?.flatMap(t => t.columns.map(c => c.column_name)) || [];
        const { password, ...safeConn } = (state.connection || {}) as any;
        await profilingApi.saveHistory({
          project_name: state.projectName,
          group_name: state.groupName,
          connection_type: state.connectorType || '',
          tables,
          table_count: tables.length,
          column_count: Array.from(new Set(columns)).length,
          rule_count: enabledRules.length,
          profile_result: state.profileResult,
          dictionary_result: state.dictionaryResult,
          suggested_rules: enabledRules,
          schema: state.selectedSchema,
          selected_tables: state.selectedTables,
          flow: {
            connector_type: state.connectorType,
            connection: safeConn,
            schema: state.selectedSchema,
            domain: state.domain,
          },
        });
      } catch {
        // Non-critical — don't block the save success
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  if (saved) {
    return (
      <div className="text-center space-y-6">
        <div className="flex items-center justify-center gap-2 mb-1">
          <CheckCircle2 className="w-8 h-8 text-cargo-success" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-cargo-text mb-2">Project Saved</h2>
          <p className="text-cargo-muted">
            <strong className="text-cargo-text">{state.projectName}</strong> has been created with{' '}
            <strong className="text-cargo-text">{saved.rules_count}</strong> rules.
          </p>
        </div>

        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6 max-w-sm mx-auto text-left space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-cargo-muted">Project ID</span>
            <span className="text-cargo-text font-mono">{saved.project_id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-cargo-muted">Group ID</span>
            <span className="text-cargo-text font-mono">{saved.group_id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-cargo-muted">Rules</span>
            <span className="text-cargo-text">{saved.rules_count}</span>
          </div>
        </div>

        <p className="text-cargo-muted text-sm">
          You can now run a quality assessment in the SDQ wizard.
        </p>

        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => navigate('/sdq')}
            className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                       font-medium py-2.5 px-6 rounded-lg transition-colors"
          >
            Run SDQ Assessment
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-cargo-panel hover:bg-cargo-hover text-cargo-text
                       font-medium py-2.5 px-6 rounded-lg transition-colors border border-cargo-border"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-4">Save to Project</h2>

      <div className="space-y-4">
        {/* Naming */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-4">
            Project Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-cargo-muted mb-1.5">
                Project Name
              </label>
              <input
                value={state.projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="My Data Quality Project"
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-cargo-border
                           text-cargo-text placeholder-cargo-muted/50 text-sm
                           focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-cargo-muted mb-1.5">
                Group Name
              </label>
              <input
                value={state.groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Main Expectation Suite"
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-cargo-border
                           text-cargo-text placeholder-cargo-muted/50 text-sm
                           focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-4">
            Summary
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard
              icon={<FolderKanban className="w-5 h-5 text-cargo-accent" />}
              label="Connector"
              value={state.connectorType || '--'}
            />
            <SummaryCard
              icon={<Table2 className="w-5 h-5 text-cargo-warning" />}
              label="Tables"
              value={`${state.selectedTables.length} selected`}
            />
            <SummaryCard
              icon={<BookOpen className="w-5 h-5 text-cargo-success" />}
              label="Rules"
              value={`${enabledRules.length} enabled`}
            />
          </div>
        </div>

        {/* Table list */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3">
            Selected Tables
          </h3>
          <div className="flex flex-wrap gap-2">
            {state.selectedTables.map((t) => (
              <span
                key={`${t.schema_name}.${t.table_name}`}
                className="px-3 py-1 bg-cargo-panel rounded-full text-sm text-cargo-text border border-cargo-border"
              >
                {t.schema_name}.{t.table_name}
              </span>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 text-cargo-error text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleSave}
          disabled={submitting || !state.projectName.trim() || !state.groupName.trim()}
          className="flex items-center gap-2 bg-cargo-success hover:bg-cargo-success/80 text-white
                     font-medium py-2.5 px-6 rounded-lg transition-colors disabled:opacity-30"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Project
        </button>
      </div>
    </div>
  );
};

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 bg-cargo-panel rounded-lg p-3">
      {icon}
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-sm font-medium text-cargo-text capitalize">{value}</p>
      </div>
    </div>
  );
}

export default SaveToProject;
